import {IAttributeData, TreeNodeWalker} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {
    IContentBlock,
    IReaderDocument,
    IReaderPublication,
    TextContentBlockType,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {
    IVanillaReaderDocumentLandmark,
    IVanillaReaderPublicationLandmark,
    IVanillaReaderReadingProgressionTimeline,
    VanillaReaderPublicationLandmarkType,
} from './VanillaReaderModel';
import {VanillaReaderPublication} from "./VanillaReaderPublication";
import {VanillaReaderLandmark} from "./VanillaReaderLandmark";

/*
* # VanillaReaderPublicationLandmarks
*
* ## RESPONSIBILITIES
*
* * ## PRIMARY COLIBRIO FRAMEWORK TYPES
*
* - IReaderPublication
* - IContentPositionTimeline
*
* ## NOTES:
*
*
* */

export class VanillaReaderPublicationLandmarkCollection {
    private _documentIndexToLandmarksMap: Map<number, IVanillaReaderDocumentLandmark> = new Map();
    private _landmarkToDocumentIndex: Map<VanillaReaderLandmark, number> = new Map();
    private _blockIdToLandmarkMap: Map<string, VanillaReaderLandmark> = new Map();
    private _hasBeenPopulated: boolean = false;
    private _colibrioReaderPublication: IReaderPublication;
    constructor(
        private _vanillaReaderPublication: VanillaReaderPublication,
        private _vanillaReadingProgressionTimeline?: IVanillaReaderReadingProgressionTimeline,
    ) {
        this._colibrioReaderPublication = this._vanillaReaderPublication.colibrioReaderPublication;
    }

    async populate(documentIndexes?: number[], landmarkTypes?: VanillaReaderPublicationLandmarkType[]): Promise<void> {
        await this._populate(this._colibrioReaderPublication, documentIndexes, landmarkTypes).catch(console.error);
    }

    getAll(): IVanillaReaderPublicationLandmark {
        let publicationLandmarkData: IVanillaReaderPublicationLandmark = {
            publicationId: this._colibrioReaderPublication.getSourcePublication().getMetadata().getIdentifiers()[0]?.content.value.toString(),
            documents: [],
        };
        let landmarks: IVanillaReaderDocumentLandmark[] = [];

        this._documentIndexToLandmarksMap.forEach((
            _doc: IVanillaReaderDocumentLandmark,
            key: number,
        ) => {
            let landmark = this._documentIndexToLandmarksMap!.get(key);
            if (landmark && landmarks.findIndex(item => item === landmark) < 0) {
                landmarks.push(landmark);
            }
        });
        publicationLandmarkData.documents = landmarks;
        return publicationLandmarkData;
    }

    getHeadings(level?: TextContentBlockType): VanillaReaderLandmark[] | undefined {
        let filteredResult: VanillaReaderLandmark[] = Array.from(this._landmarkToDocumentIndex.keys());
        let headings = filteredResult.filter((item) => {
            if (level) {
                return item.blockType.indexOf(level) >= 0;
            } else {
                return item.blockType.indexOf('HEADING') >= 0;
            }
        });
        return headings.length > 0 ? headings : undefined;
    }

    getFootnotes(): VanillaReaderLandmark[] | undefined {
        let filteredResult: VanillaReaderLandmark[] = Array.from(this._landmarkToDocumentIndex.keys());
        let footnotes = filteredResult.filter((item) => {
            return item.subType && item.subType.toString().indexOf('FOOTNOTE') >= 0;
        });

        return footnotes.length > 0 ? footnotes : undefined;
    }

    getFigures(): VanillaReaderLandmark[] | undefined {
        let filteredResult: VanillaReaderLandmark[] = Array.from(this._landmarkToDocumentIndex.keys());
        let figures = filteredResult.filter((item) => {
            return item.blockType.includes('IMAGE') || item.blockType.includes('FIGURE');
        });
        return figures.length > 0 ? figures : undefined;
    }

    getTables(): VanillaReaderLandmark[] | undefined {
        let filteredResult: VanillaReaderLandmark[] = Array.from(this._landmarkToDocumentIndex.keys());
        let tables = filteredResult.filter((item) => {
            return item.blockType === 'TABLE';
        });
        return tables.length > 0 ? tables : undefined;
    }

    getLists(): VanillaReaderLandmark[] | undefined {
        let filteredResult: VanillaReaderLandmark[] = Array.from(this._landmarkToDocumentIndex.keys());
        let lists = filteredResult.filter((item) => {
            return item.blockType.includes('_LIST');
        });
        return lists.length ? lists : undefined;
    }

    private async _populate(
        readerPublication: IReaderPublication,
        documentIndexes?: number[],
        landmarkTypes?: VanillaReaderPublicationLandmarkType[],
    ): Promise<void> {

        if (this._hasBeenPopulated) {
            return;
        }

        let documentsToSearch = readerPublication.getSpine();

        // Check if we should search only some elements as listed in the `documentIndexes` argument.
        if (documentIndexes) {
            documentsToSearch = readerPublication.getSpine().filter((doc: IReaderDocument) => {
                return documentIndexes.includes(doc.getSourceContentDocument().getIndexInSpine());
            });
        }

        for (const doc of documentsToSearch) {
            let documentIndex = doc.getSourceContentDocument().getIndexInSpine();

            if (this._documentIndexToLandmarksMap!.has(documentIndex)) {
                continue;
            }

            let docLandmarks: IVanillaReaderDocumentLandmark = {
                documentIndex
            };

            let contentBlockTree = await doc.fetchContentBlockTree();

            let walker = new TreeNodeWalker(contentBlockTree.getContentBlocks());

            while (walker.hasNext()) {
                let addBlock: boolean = false;
                let block = walker.next();
                let blockType = (block.getBlockType() as unknown) as VanillaReaderPublicationLandmarkType;
                let collectionToAddTo: VanillaReaderLandmark[] | undefined;
                let blockAttributes: IAttributeData[] = [];
                let currentLandmarkData: VanillaReaderLandmark | undefined;

                if (!landmarkTypes) {
                    landmarkTypes = (Object.values(VanillaReaderPublicationLandmarkType) as unknown) as VanillaReaderPublicationLandmarkType[];
                }

                if (landmarkTypes.includes(blockType)) {
                    switch (blockType) {
                        case VanillaReaderPublicationLandmarkType.ARTICLE:
                            addBlock = true;
                            docLandmarks.articles = [];
                            collectionToAddTo = docLandmarks.articles;
                            break;
                        case VanillaReaderPublicationLandmarkType.SECTION:
                            addBlock = true;
                            docLandmarks.sections = [];
                            collectionToAddTo = docLandmarks.sections;
                            break;
                        case VanillaReaderPublicationLandmarkType.ASIDE:
                            addBlock = true;
                            docLandmarks.asides = undefined;
                            collectionToAddTo = docLandmarks.asides;
                            break;
                        case VanillaReaderPublicationLandmarkType.HEADING1:
                        case VanillaReaderPublicationLandmarkType.HEADING2:
                        case VanillaReaderPublicationLandmarkType.HEADING3:
                        case VanillaReaderPublicationLandmarkType.HEADING4:
                        case VanillaReaderPublicationLandmarkType.HEADING5:
                        case VanillaReaderPublicationLandmarkType.HEADING6:
                            addBlock = true;
                            docLandmarks.headings = [];
                            collectionToAddTo = docLandmarks.headings;
                            break;
                        case VanillaReaderPublicationLandmarkType.AUDIO:
                            addBlock = true;
                            docLandmarks.audio = [];
                            collectionToAddTo = docLandmarks.audio;
                            break;
                        case VanillaReaderPublicationLandmarkType.VIDEO:
                            addBlock = true;
                            docLandmarks.video = [];
                            collectionToAddTo = docLandmarks.video;
                            break;
                        case VanillaReaderPublicationLandmarkType.IMAGE:
                        case VanillaReaderPublicationLandmarkType.FIGURE:
                            addBlock = true;
                            docLandmarks.figures = [];
                            collectionToAddTo = docLandmarks.figures;
                            break;
                        case VanillaReaderPublicationLandmarkType.TABLE:
                            addBlock = true;
                            docLandmarks.tables = [];
                            collectionToAddTo = docLandmarks.tables;
                            break;
                        case VanillaReaderPublicationLandmarkType.DESCRIPTION_LIST:
                        case VanillaReaderPublicationLandmarkType.UNORDERED_LIST:
                        case VanillaReaderPublicationLandmarkType.ORDERED_LIST:
                            addBlock = true;
                            docLandmarks.lists = [];
                            collectionToAddTo = docLandmarks.lists;
                            break;
                        default:
                    }
                }

                if (landmarkTypes.includes(VanillaReaderPublicationLandmarkType.FOOTNOTE)) {
                    docLandmarks.footnotes = [];
                    currentLandmarkData = await this._fetchLandmarkForContentBlock(block);

                    if(currentLandmarkData.subType === VanillaReaderPublicationLandmarkType.FOOTNOTE) {
                        addBlock = true;
                        docLandmarks.footnotes.push(currentLandmarkData);
                    }
                }

                if (addBlock) {

                    collectionToAddTo = collectionToAddTo ? collectionToAddTo : [];
                    if(!currentLandmarkData) {
                        currentLandmarkData = await this._fetchLandmarkForContentBlock(block);
                    }

                    collectionToAddTo.push(currentLandmarkData);

                    this._landmarkToDocumentIndex.set(currentLandmarkData, documentIndex);

                    let blockIdAttribute = blockAttributes.find((attribute: IAttributeData) => {
                        return attribute.localName === 'id';
                    });

                    if (blockIdAttribute && !this._blockIdToLandmarkMap.has(blockIdAttribute.value)) {
                        this._blockIdToLandmarkMap.set(blockIdAttribute.value, currentLandmarkData);
                    }

                    currentLandmarkData = undefined;
                }
            }

            this._documentIndexToLandmarksMap!.set(documentIndex, docLandmarks);

            this._hasBeenPopulated = true;

        }
    }

    private async _fetchLandmarkForContentBlock(
        block: IContentBlock,
    ): Promise<VanillaReaderLandmark> {
        return new VanillaReaderLandmark(block, this._vanillaReaderPublication.navigationTree.getTreeData(), this._vanillaReadingProgressionTimeline) ;
    }

}
